import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Download, Printer, RefreshCw, Wallet } from 'lucide-react';
import { INVOICE_STATUS, TALLY_STATUS, dateLabel, dateTimeLabel, todayIso } from '@adisys/shared';
import { api, request, ApiRequestError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Fact, MetaBadge, Notice, errorText, inr2, qtyLabel } from '../../lib/crm';
import { PageHeader } from '../../components/AppShell';
import { Button, Card, CardHeader, ErrorState, Field, Input, Modal, Select, Skeleton, Textarea, cx, useToast } from '../../components/ui';

const MODES = [
  ['bank_transfer', 'Bank transfer'], ['cheque', 'Cheque'], ['upi', 'UPI'], ['cash', 'Cash'], ['card', 'Card'], ['other', 'Other'],
] as const;

export function InvoiceDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const key = ['crm', 'invoice', id];
  const inv = useQuery({ queryKey: key, queryFn: () => api.get(`/crm/invoices/${id}`) });
  const summary = useQuery({ queryKey: ['crm', 'finance', 'summary'], queryFn: () => api.get('/crm/finance/summary') });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['crm'] }); };

  const [payOpen, setPayOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [pay, setPay] = useState({ amount: '', paidOn: todayIso(), mode: 'bank_transfer', reference: '', note: '' });
  const [reason, setReason] = useState('');
  const [error, setError] = useState<unknown>(null);
  const fe = (k: string) => (error instanceof ApiRequestError ? error.fieldError(k) : undefined);

  const record = useMutation({
    mutationFn: () => api.post(`/crm/invoices/${id}/payments`, {
      amount: Number(pay.amount), paidOn: pay.paidOn || undefined, mode: pay.mode,
      reference: pay.reference || undefined, note: pay.note || undefined }),
    onSuccess: () => { toast.success('Payment recorded'); setPayOpen(false); setError(null); refresh(); },
    onError: (e) => setError(e),
  });
  const cancel = useMutation({
    mutationFn: () => api.post(`/crm/invoices/${id}/cancel`, { reason }),
    onSuccess: () => { toast.success('Invoice cancelled'); setCancelOpen(false); setError(null); refresh(); },
    onError: (e) => setError(e),
  });
  const sync = useMutation({
    mutationFn: () => api.post(`/crm/invoices/${id}/tally`),
    onSuccess: (r: any) => {
      if (r.status === 'synced') toast.success('Sent to Tally');
      else if (r.status === 'queued') toast.info('Voucher queued', 'No Tally endpoint is configured. Download the XML to import by hand.');
      else toast.error('Tally rejected it', r.error ?? undefined);
      refresh();
    },
    onError: (e) => toast.error('Tally sync failed', errorText(e)),
  });

  const downloadXml = async () => {
    try {
      const res = await request<Response>(`/crm/invoices/${id}/tally-xml`, { raw: true });
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a'); a.href = url; a.download = `${inv.data.invoiceNo}.xml`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed', errorText(e)); }
  };

  if (inv.isLoading) return <div className="space-y-4 p-6"><Skeleton className="h-10 w-72" /><Skeleton className="h-64 w-full" /></div>;
  if (inv.isError) return <ErrorState error={inv.error} onRetry={() => inv.refetch()} />;
  const i = inv.data;
  const open = ['issued', 'partially_paid'].includes(i.status);
  const manage = can('crm.finance.manage');

  return (
    <>
      <PageHeader title={i.invoiceNo}
        description={`${i.customerName} · ${i.projectName}`}
        actions={
          <>
            <Link to={`/crm/finance/invoices/${id}/document`} target="_blank">
              <Button icon={<Printer className="h-4 w-4" />}>Print / PDF</Button></Link>
            {manage && open && <Button variant="primary" icon={<Wallet className="h-4 w-4" />} onClick={() => { setError(null); setPay({ ...pay, amount: String(i.outstanding) }); setPayOpen(true); }}>Record payment</Button>}
            {manage && i.status !== 'cancelled' && Number(i.paidAmount) === 0 && (
              <Button variant="danger" icon={<Ban className="h-4 w-4" />} onClick={() => { setError(null); setCancelOpen(true); }}>Cancel</Button>)}
          </>
        }>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <MetaBadge meta={INVOICE_STATUS} value={i.status} />
          <Link className="text-brand-600 hover:underline" to={`/crm/projects/${i.projectId}?tab=finance`}>{i.projectCode}</Link>
          <span className="text-ink-400">·</span><span className="text-ink-600">{i.orderNo}</span>
          {i.isOverdue && <span className="font-medium text-danger">{i.daysOverdue} days overdue</span>}
        </div>
      </PageHeader>

      <div className="grid gap-5 p-4 sm:p-6 xl:grid-cols-3">
        <div className="space-y-5 xl:col-span-2">
          <Card>
            <dl className="grid gap-4 sm:grid-cols-3">
              <Fact label="Invoice date">{dateLabel(i.invoiceDate)}</Fact>
              <Fact label="Due date"><span className={cx(i.isOverdue && 'text-danger')}>{dateLabel(i.dueDate)}</span></Fact>
              <Fact label="Place of supply">{i.placeOfSupply}</Fact>
              <Fact label="Bill to">{i.billTo?.company}</Fact>
              <Fact label="GSTIN">{i.billTo?.gstin}</Fact>
              <Fact label="Address">{i.billTo?.address}</Fact>
            </dl>
            {i.note && <p className="mt-4 whitespace-pre-line text-sm text-ink-600">{i.note}</p>}
          </Card>

          <Card padded={false} className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead className="bg-sunken/60 text-xs text-ink-600">
                  <tr><th className="px-3 py-2.5">Description</th><th className="px-3 py-2.5 text-right">Qty</th><th className="px-3 py-2.5 text-right">Rate</th>
                    <th className="px-3 py-2.5 text-right">Taxable</th><th className="px-3 py-2.5 text-right">GST</th><th className="px-3 py-2.5 text-right">Total</th></tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {i.items.map((l: any) => (
                    <tr key={l.id}>
                      <td className="px-3 py-2.5"><p className="font-medium text-ink-900">{l.description}</p>
                        <p className="text-xs text-ink-500">{[l.modelNumber, l.hsnCode && `HSN ${l.hsnCode}`].filter(Boolean).join(' · ')}</p></td>
                      <td className="tabular px-3 py-2.5 text-right">{qtyLabel(l.quantity)} {l.unit}</td>
                      <td className="tabular px-3 py-2.5 text-right">{inr2(l.unitPrice)}</td>
                      <td className="tabular px-3 py-2.5 text-right">{inr2(l.taxable)}</td>
                      <td className="tabular px-3 py-2.5 text-right">{inr2(l.gst)} <span className="text-xs text-ink-500">({qtyLabel(l.gstRate)}%)</span></td>
                      <td className="tabular px-3 py-2.5 text-right font-medium">{inr2(l.total)}</td>
                    </tr>))}
                </tbody>
              </table>
            </div>
            <dl className="tabular ml-auto w-full max-w-xs space-y-1 border-t border-line p-4 text-sm">
              <div className="flex justify-between"><dt className="text-ink-500">Taxable value</dt><dd>{inr2(i.taxableTotal)}</dd></div>
              <div className="flex justify-between"><dt className="text-ink-500">GST</dt><dd>{inr2(i.gstTotal)}</dd></div>
              <div className="flex justify-between border-t border-line pt-1 font-semibold text-ink-900"><dt>Total</dt><dd>{inr2(i.grandTotal)}</dd></div>
              <div className="flex justify-between"><dt className="text-ink-500">Paid</dt><dd>{inr2(i.paidAmount)}</dd></div>
              <div className="flex justify-between font-semibold"><dt>Outstanding</dt><dd>{inr2(i.outstanding)}</dd></div>
            </dl>
          </Card>
        </div>

        <div className="space-y-5">
          <Card>
            <CardHeader title="Payments" />
            {i.payments.length === 0 ? <p className="text-sm text-ink-500">No payment recorded yet.</p> : (
              <ul className="divide-y divide-line">
                {i.payments.map((p: any) => (
                  <li key={p.id} className="flex items-start justify-between gap-3 py-2.5 text-sm">
                    <div><p className="font-medium text-ink-900">{inr2(p.amount)}</p>
                      <p className="text-xs text-ink-500">{dateLabel(p.paidOn)} · {String(p.mode).replace('_', ' ')}{p.reference ? ` · ${p.reference}` : ''}</p>
                      {p.note && <p className="text-xs text-ink-500">{p.note}</p>}</div>
                    <span className="shrink-0 text-xs text-ink-400">{p.recordedByName}</span>
                  </li>))}
              </ul>)}
          </Card>

          <Card>
            <CardHeader title="Tally" action={<MetaBadge meta={TALLY_STATUS} value={i.tallyStatus} />} />
            {summary.data && !summary.data.tallyConfigured && (
              <Notice tone="info">No Tally endpoint is configured (TALLY_URL). Vouchers are prepared and kept queued; download the XML to import into TallyPrime / TallyZen.</Notice>)}
            <div className="mt-3 flex flex-wrap gap-2">
              {manage && i.status !== 'cancelled' && i.tallyStatus !== 'synced' && (
                <Button size="sm" icon={<RefreshCw className="h-3.5 w-3.5" />} loading={sync.isPending} onClick={() => sync.mutate()}>Sync to Tally</Button>)}
              <Button size="sm" icon={<Download className="h-3.5 w-3.5" />} onClick={downloadXml}>Download XML</Button>
            </div>
            {i.tallyLog.length > 0 && (
              <ul className="mt-3 space-y-1.5 text-xs">
                {i.tallyLog.map((t: any) => (
                  <li key={t.id} className="rounded-lg bg-ink-50 p-2">
                    <span className="font-medium">{t.status}</span> · attempt {t.attempts} · {dateTimeLabel(t.createdAt)}
                    {t.error && <span className="block text-danger">{t.error}</span>}
                  </li>))}
              </ul>)}
          </Card>
        </div>
      </div>

      <Modal open={payOpen} onClose={() => setPayOpen(false)} title="Record payment" description={`Outstanding: ${inr2(i.outstanding)}`}
        footer={<><Button onClick={() => setPayOpen(false)}>Cancel</Button>
          <Button variant="primary" loading={record.isPending} onClick={() => record.mutate()}>Save payment</Button></>}>
        <div className="space-y-3">
          {error instanceof ApiRequestError && !fe('amount') && <Notice tone="danger">{error.message}</Notice>}
          <Field label="Amount" required error={fe('amount')}>
            <Input type="number" min={0} step="0.01" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Received on"><Input type="date" value={pay.paidOn} onChange={(e) => setPay({ ...pay, paidOn: e.target.value })} /></Field>
            <Field label="Mode"><Select value={pay.mode} onChange={(e) => setPay({ ...pay, mode: e.target.value })}>
              {MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></Field>
          </div>
          <Field label="Reference (UTR / cheque no.)"><Input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} /></Field>
          <Field label="Note"><Textarea rows={2} value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} /></Field>
        </div>
      </Modal>

      <Modal open={cancelOpen} onClose={() => setCancelOpen(false)} title="Cancel invoice" size="sm"
        description="The billed quantities become available to invoice again."
        footer={<><Button onClick={() => setCancelOpen(false)}>Keep invoice</Button>
          <Button variant="danger" loading={cancel.isPending} onClick={() => cancel.mutate()}>Cancel invoice</Button></>}>
        <div className="space-y-3">
          {error instanceof ApiRequestError && !fe('reason') && <Notice tone="danger">{error.message}</Notice>}
          <Field label="Reason" required error={fe('reason')}>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        </div>
      </Modal>
    </>
  );
}
